import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AtSign, Check, X, ArrowRight } from 'lucide-react';

/**
 * Inline username setup — for the Preferences page (or wherever the user
 * manages their identity). Real-time availability check while typing.
 */
export default function UsernameSetup({ compact = false }) {
  const { user, refresh } = useAuth();
  const [u, setU] = useState('');
  const [status, setStatus] = useState('idle'); // idle | checking | ok | bad
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user?.username) setU(user.username); }, [user]);

  useEffect(() => {
    const norm = u.trim().replace(/^@/, '').toLowerCase();
    if (!norm) { setStatus('idle'); setMsg(''); return; }
    if (norm === user?.username) { setStatus('ok'); setMsg('This is your current username'); return; }
    setStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/user/username/check?u=${encodeURIComponent(norm)}`);
        if (r.data.available) { setStatus('ok'); setMsg('Available'); }
        else { setStatus('bad'); setMsg(r.data.message || 'Not available'); }
      } catch (_e) { setStatus('bad'); setMsg('Check failed'); }
    }, 350);
    return () => clearTimeout(t);
  }, [u, user]);

  const save = async () => {
    const norm = u.trim().replace(/^@/, '').toLowerCase();
    if (!norm || status !== 'ok') return;
    if (norm === user?.username) { toast.info('Username unchanged'); return; }
    setBusy(true);
    try {
      await api.put('/user/username', { username: norm });
      toast.success(`Username set to @${norm}`);
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set username');
    } finally { setBusy(false); }
  };

  return (
    <div className={compact ? '' : 'border border-[#2B3139] bg-[#181A20] p-4 rounded-sm'} data-testid="username-setup">
      {!compact && (
        <>
          <div className="text-xs uppercase font-mono text-[#FCD535] mb-1">Username</div>
          <div className="text-[11px] text-[#B7BDC6] mb-3">
            Pick a unique @handle so friends can send you crypto by name (e.g. <span className="font-mono">@yourname</span>).
            {user?.username && <> Your current handle: <span className="font-mono text-[#0ECB81]">@{user.username}</span></>}
          </div>
        </>
      )}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <AtSign className="w-3.5 h-3.5 text-[#848E9C] absolute left-2 top-1/2 -translate-y-1/2" />
          <Input value={u} onChange={e => setU(e.target.value)} placeholder="yourname"
            className="pl-7 bg-[#0B0E11] border-[#2B3139] rounded-sm font-mono text-white h-10 focus-visible:border-[#FCD535]"
            data-testid="username-input" />
          {status === 'ok' && <Check className="w-4 h-4 text-[#0ECB81] absolute right-2 top-1/2 -translate-y-1/2" />}
          {status === 'bad' && <X className="w-4 h-4 text-[#F6465D] absolute right-2 top-1/2 -translate-y-1/2" />}
        </div>
        <Button onClick={save} disabled={busy || status !== 'ok' || u.trim().replace(/^@/, '').toLowerCase() === user?.username}
          className="bg-[#FCD535] hover:bg-[#F0B90B] disabled:opacity-50 text-[#0B0E11] rounded-sm font-semibold px-4"
          data-testid="username-save-btn">
          {busy ? '…' : (user?.username ? 'Update' : 'Set')} <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
      {msg && (
        <div className={`text-[11px] mt-1.5 font-mono ${status === 'ok' ? 'text-[#0ECB81]' : status === 'bad' ? 'text-[#F6465D]' : 'text-[#848E9C]'}`}
          data-testid="username-msg">
          {msg}
        </div>
      )}
      {!compact && (
        <div className="text-[10px] text-[#5E6673] font-mono mt-2">
          3-20 characters · lowercase letters, numbers, and _ only · cannot use reserved words
        </div>
      )}
    </div>
  );
}
